import React, { useState } from 'react';
import { X, Clock, DollarSign, User, Calendar, FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * OvertimeApprovalModal
 *
 * Modal for corporate users to review and approve/reject overtime requests.
 * Supports full approval, partial approval, or rejection with reason.
 */
export default function OvertimeApprovalModal({
  request,
  onApprove,
  onReject,
  onClose,
  submitting = false
}) {
  const [approvalType, setApprovalType] = useState('full'); // 'full', 'partial', 'reject'
  const [partialAmount, setPartialAmount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    if (approvalType === 'reject') {
      onReject(rejectionReason);
    } else {
      const amount = approvalType === 'full'
        ? request?.requested_overtime_amount
        : parseFloat(partialAmount);
      onApprove(amount);
    }
  };

  if (!request) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '0';
    return parseFloat(hours).toFixed(1);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-background w-full max-w-2xl rounded-xl overflow-hidden border border-border max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-orange-500/10 sticky top-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Overtime Request Review</h2>
              <p className="text-orange-300 text-sm">Review and approve/reject this request</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Job & Contractor Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-surface border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <User size={16} className="text-text-secondary" />
                <p className="text-text-secondary text-xs">Contractor</p>
              </div>
              <p className="text-text-primary font-medium">
                {request?.contractor?.first_name} {request?.contractor?.last_name}
              </p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={16} className="text-text-secondary" />
                <p className="text-text-secondary text-xs">Request Date</p>
              </div>
              <p className="text-text-primary font-medium">{formatDate(request?.created_at)}</p>
            </div>
          </div>

          {/* Job Details */}
          {request?.job_instance?.jobs && (
            <div className="bg-surface border border-border rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-text-secondary mb-2">Job Details</h3>
              <p className="text-text-primary font-medium">{request?.job_instance?.jobs?.job_name}</p>
              <p className="text-text-secondary text-sm mt-1">
                {request?.job_instance?.jobs?.job_site?.address || request?.job_instance?.jobs?.location_address || 'No address'}
              </p>
            </div>
          )}

          {/* Hours Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-surface border border-border rounded-lg p-4 text-center">
              <p className="text-text-secondary text-xs mb-1">Estimated</p>
              <p className="text-text-primary text-xl font-bold">{formatHours(request?.estimated_hours)}h</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-4 text-center">
              <p className="text-text-secondary text-xs mb-1">Actual</p>
              <p className="text-blue-400 text-xl font-bold">{formatHours(request?.actual_hours)}h</p>
            </div>
            <div className="bg-surface border border-orange-500/50 rounded-lg p-4 text-center">
              <p className="text-text-secondary text-xs mb-1">Overtime</p>
              <p className="text-orange-400 text-xl font-bold">{formatHours(request?.overtime_hours)}h</p>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-surface border border-border rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Payment Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Hourly Rate</span>
                <span className="text-text-primary font-medium">{formatCurrency(request?.hourly_rate)}/hr</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Base Pay (capped at {formatHours(request?.estimated_hours)}h)</span>
                <span className="text-text-primary font-medium">{formatCurrency(request?.base_pay)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-orange-400">
                  Overtime Requested ({formatHours(request?.overtime_hours)}h × {formatCurrency(request?.hourly_rate)})
                </span>
                <span className="text-orange-400 font-medium">
                  {formatCurrency(request?.requested_overtime_amount)}
                </span>
              </div>
              <div className="border-t border-border-light pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Total if Approved</span>
                  <span className="text-green-400 font-bold">
                    {formatCurrency(parseFloat(request?.base_pay || 0) + parseFloat(request?.requested_overtime_amount || 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contractor's Justification */}
          <div className="bg-surface border border-border rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={16} className="text-text-secondary" />
              <h3 className="text-sm font-medium text-text-secondary">Contractor's Justification</h3>
            </div>
            <p className="text-text-primary text-sm leading-relaxed">
              {request?.justification || 'No justification provided'}
            </p>
          </div>

          {/* Approval Options */}
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-medium text-text-secondary">Your Decision</h3>

            {/* Full Approval */}
            <label
              className={cn(
                'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                approvalType === 'full'
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-border hover:border-border-light'
              )}
            >
              <input
                type="radio"
                name="approvalType"
                value="full"
                checked={approvalType === 'full'}
                onChange={(e) => setApprovalType(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-400" />
                  <span className="text-text-primary font-medium">Approve Full Amount</span>
                </div>
                <p className="text-text-secondary text-sm mt-1">
                  Approve the full overtime amount of {formatCurrency(request?.requested_overtime_amount)}
                </p>
              </div>
            </label>

            {/* Partial Approval */}
            <label
              className={cn(
                'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                approvalType === 'partial'
                  ? 'border-yellow-500 bg-yellow-500/10'
                  : 'border-border hover:border-border-light'
              )}
            >
              <input
                type="radio"
                name="approvalType"
                value="partial"
                checked={approvalType === 'partial'}
                onChange={(e) => setApprovalType(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={18} className="text-yellow-400" />
                  <span className="text-text-primary font-medium">Partial Approval</span>
                </div>
                <p className="text-text-secondary text-sm mt-1">
                  Approve a custom amount less than the requested overtime
                </p>
                {approvalType === 'partial' && (
                  <div className="mt-3">
                    <label className="text-xs text-text-secondary mb-1 block">Approved Amount</label>
                    <div className="relative">
                      <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={request?.requested_overtime_amount}
                        value={partialAmount}
                        onChange={(e) => setPartialAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-8 pr-4 py-2 bg-border border border-border-light rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-yellow-500"
                        required={approvalType === 'partial'}
                      />
                    </div>
                    <p className="text-xs text-text-secondary mt-1">
                      Max: {formatCurrency(request?.requested_overtime_amount)}
                    </p>
                  </div>
                )}
              </div>
            </label>

            {/* Rejection */}
            <label
              className={cn(
                'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                approvalType === 'reject'
                  ? 'border-red-500 bg-red-500/10'
                  : 'border-border hover:border-border-light'
              )}
            >
              <input
                type="radio"
                name="approvalType"
                value="reject"
                checked={approvalType === 'reject'}
                onChange={(e) => setApprovalType(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <XCircle size={18} className="text-red-400" />
                  <span className="text-text-primary font-medium">Reject Request</span>
                </div>
                <p className="text-text-secondary text-sm mt-1">
                  Deny the overtime request. Contractor will only receive base pay.
                </p>
                {approvalType === 'reject' && (
                  <div className="mt-3">
                    <label className="text-xs text-text-secondary mb-1 block">
                      Rejection Reason <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Explain why this overtime request is being rejected..."
                      rows={3}
                      className="w-full px-4 py-2 bg-border border border-border-light rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-red-500 resize-none"
                      required={approvalType === 'reject'}
                    />
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                submitting ||
                (approvalType === 'partial' && (!partialAmount || parseFloat(partialAmount) <= 0)) ||
                (approvalType === 'reject' && !rejectionReason.trim())
              }
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors',
                approvalType === 'reject'
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {submitting ? (
                'Processing...'
              ) : approvalType === 'reject' ? (
                <>
                  <XCircle size={18} />
                  Reject Request
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  {approvalType === 'full' ? 'Approve Full Amount' : 'Approve Partial'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
